import { useState, useEffect } from 'react'
import { adminAPI, type NfcOperationResponse } from '@/lib/api/admin'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'

interface NfcTag {
  id: number
  uid: string
  displayUid: string
  claimCode?: string
  status: 'READY' | 'BOUND' | 'ACTIVATED' | 'LOST' | 'REVOKED'
  availableForAssignment: boolean
  createdAt: string
}

interface NfcManagementCardProps {
  listingId?: string
  currentNfcUid?: string
  onNfcBound: (uid: string) => void
  onBindingStateChange: (isBound: boolean) => void
}

type BindingMode = 'pool' | 'manual' | 'phone' | null

export default function NfcManagementCard({
  listingId,
  currentNfcUid,
  onNfcBound,
  onBindingStateChange
}: NfcManagementCardProps) {
  const [bindingMode, setBindingMode] = useState<BindingMode>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Pool selection state
  const [availableTags, setAvailableTags] = useState<NfcTag[]>([])
  const [selectedTagId, setSelectedTagId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  
  // Manual input state
  const [manualUid, setManualUid] = useState('')
  const [manualClaimCode, setManualClaimCode] = useState('')
  const [useClaimCode, setUseClaimCode] = useState(false)

  // QR code state
  const [qrCodeUrl, setQrCodeUrl] = useState('')

  useEffect(() => {
    onBindingStateChange(!!currentNfcUid)
  }, [currentNfcUid, onBindingStateChange])

  const loadAvailableTags = async () => {
    try {
      setLoading(true)
      setError('')
      
      const tags = await adminAPI.getAvailableNfcTags()
      setAvailableTags(tags)
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load available tags')
      setAvailableTags([])
    } finally {
      setLoading(false)
    }
  }

  const handlePoolSelection = () => {
    setBindingMode('pool')
    setError('')
    setSuccess('')
    loadAvailableTags()
  }

  const handleManualEntry = () => {
    setBindingMode('manual')
    setError('')
    setSuccess('')
    setManualUid('')
    setManualClaimCode('')
    setUseClaimCode(false)
  }

  const handlePhoneBinding = async () => {
    if (!listingId) {
      setError('Listing ID required for phone binding')
      return
    }

    try {
      setBindingMode('phone')
      setError('')
      setSuccess('')
      setLoading(true)
      
      const qrData = await adminAPI.generateNfcBindingQr(listingId)
      setQrCodeUrl(qrData.qrCodeUrl)
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate QR code')
    } finally {
      setLoading(false)
    }
  }

  const bindFromPool = async () => {
    if (!selectedTagId || !listingId) {
      setError('Please select a tag and ensure listing is saved')
      return
    }

    try {
      setLoading(true)
      setError('')
      setSuccess('')
      
      const selectedTag = availableTags.find(tag => tag.id.toString() === selectedTagId)
      if (!selectedTag) {
        setError('Selected tag not found')
        return
      }

      const result: NfcOperationResponse = await adminAPI.bindNfcToListing(listingId, { uid: selectedTag.uid })
      
      if (result.success) {
        setSuccess(result.message || `Successfully bound NFC tag ${selectedTag.displayUid}`)
        onNfcBound(selectedTag.uid)
        setBindingMode(null)
      } else {
        setError(result.message || 'Failed to bind NFC tag')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bind NFC tag')
    } finally {
      setLoading(false)
    }
  }

  const bindManually = async () => {
    if (!listingId) {
      setError('Listing must be saved before binding NFC tag')
      return
    }

    if (!useClaimCode && !manualUid.trim()) {
      setError('Please enter a valid NFC UID')
      return
    }

    if (useClaimCode && !manualClaimCode.trim()) {
      setError('Please enter a valid claim code')
      return
    }

    try {
      setLoading(true)
      setError('')
      setSuccess('')

      const bindRequest = useClaimCode 
        ? { claimCode: manualClaimCode.trim() }
        : { uid: manualUid.trim() }
      const result: NfcOperationResponse = await adminAPI.bindNfcToListing(listingId, bindRequest)
      
      if (result.success) {
        const displayUid = useClaimCode 
          ? `Tag with claim code ${manualClaimCode}` 
          : manualUid.replace(/(.{2})/g, '$1:').replace(/:$/, '').toUpperCase()
        
        setSuccess(result.message || `Successfully bound NFC tag ${displayUid}`)
        onNfcBound(useClaimCode ? manualClaimCode : manualUid)
        setBindingMode(null)
      } else {
        setError(result.message || 'Failed to bind NFC tag')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bind NFC tag')
    } finally {
      setLoading(false)
    }
  }

  const filteredTags = availableTags.filter(tag =>
    tag.displayUid.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tag.claimCode && tag.claimCode.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const resetBinding = () => {
    setBindingMode(null)
    setError('')
    setSuccess('')
    setSelectedTagId('')
    setSearchQuery('')
    setManualUid('')
    setManualClaimCode('')
    setQrCodeUrl('')
  }

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-purple-900 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          NFC Tag Management
        </h4>
        {currentNfcUid && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
            <span className="text-sm font-medium text-green-700">Bound</span>
          </div>
        )}
      </div>

      {/* Current Status */}
      <div className="mb-4">
        {currentNfcUid ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <p className="text-sm text-green-800">
              <strong>Bound to:</strong> <code className="font-mono bg-green-100 px-1 rounded">{currentNfcUid}</code>
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              <strong>Status:</strong> Unbound - All listings must have an NFC tag
            </p>
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* Binding Mode Selection */}
      {!bindingMode && !currentNfcUid && (
        <div className="space-y-3">
          <p className="text-sm text-purple-700 mb-4">
            Choose how to bind an NFC tag to this listing:
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={handlePhoneBinding}
              className="flex flex-col items-center p-4 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <svg className="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11V9a6 6 0 10-12 0v7m-3 0h18m-9 4v-4m0 0V9a3 3 0 016 0v8a3 3 0 01-6 0z" />
              </svg>
              <span className="font-medium text-purple-900">Bind with Phone</span>
              <span className="text-xs text-purple-600">QR code scan</span>
            </button>

            <button
              onClick={handlePoolSelection}
              className="flex flex-col items-center p-4 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <svg className="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span className="font-medium text-purple-900">Select from Pool</span>
              <span className="text-xs text-purple-600">Available tags</span>
            </button>

            <button
              onClick={handleManualEntry}
              className="flex flex-col items-center p-4 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <svg className="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="font-medium text-purple-900">Manual Entry</span>
              <span className="text-xs text-purple-600">UID or claim code</span>
            </button>
          </div>
        </div>
      )}

      {/* Phone Binding Mode */}
      {bindingMode === 'phone' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-purple-900">Bind with Phone</h5>
            <button
              onClick={resetBinding}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              Cancel
            </button>
          </div>
          
          {qrCodeUrl ? (
            <div className="text-center">
              <img src={qrCodeUrl} alt="NFC Binding QR Code" className="mx-auto mb-4 border rounded-lg" />
              <p className="text-sm text-purple-700">
                Scan this QR code with your phone to bind an NFC tag to this listing
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-sm text-purple-700 mt-2">Generating QR code...</p>
            </div>
          )}
        </div>
      )}

      {/* Pool Selection Mode */}
      {bindingMode === 'pool' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-purple-900">Select from Tag Pool</h5>
            <button
              onClick={resetBinding}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-3">
            <Input
              placeholder="Search by UID or claim code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />

            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading available tags...</p>
                </div>
              ) : filteredTags.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <p className="text-sm">No available tags found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredTags.map((tag) => (
                    <label
                      key={tag.id}
                      className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="selectedTag"
                        value={tag.id.toString()}
                        checked={selectedTagId === tag.id.toString()}
                        onChange={(e) => setSelectedTagId(e.target.value)}
                        className="mr-3 text-purple-600"
                      />
                      <div className="flex-1">
                        <div className="font-mono text-sm font-medium">
                          {tag.displayUid}
                        </div>
                        {tag.claimCode && (
                          <div className="text-xs text-gray-500">
                            Claim: {tag.claimCode}
                          </div>
                        )}
                        <div className="text-xs text-gray-400">
                          Created: {new Date(tag.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                onClick={bindFromPool}
                disabled={!selectedTagId || loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? 'Binding...' : 'Bind Selected Tag'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Mode */}
      {bindingMode === 'manual' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-purple-900">Manual Entry</h5>
            <button
              onClick={resetBinding}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="entryType"
                  checked={!useClaimCode}
                  onChange={() => setUseClaimCode(false)}
                  className="mr-2 text-purple-600"
                />
                <span className="text-sm font-medium text-gray-700">NFC UID</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="entryType"
                  checked={useClaimCode}
                  onChange={() => setUseClaimCode(true)}
                  className="mr-2 text-purple-600"
                />
                <span className="text-sm font-medium text-gray-700">Claim Code</span>
              </label>
            </div>

            {!useClaimCode ? (
              <Input
                placeholder="NFC Tag UID (e.g., 04:A1:B2:C3:D4:E5:F6)"
                value={manualUid}
                onChange={(e) => setManualUid(e.target.value)}
                className="font-mono"
              />
            ) : (
              <Input
                placeholder="Claim Code (e.g., ABC123)"
                value={manualClaimCode}
                onChange={(e) => setManualClaimCode(e.target.value.toUpperCase())}
                className="font-mono"
              />
            )}

            <div className="flex justify-end">
              <Button
                onClick={bindManually}
                disabled={loading || (!useClaimCode && !manualUid.trim()) || (useClaimCode && !manualClaimCode.trim())}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? 'Binding...' : 'Bind NFC Tag'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Already Bound Actions */}
      {currentNfcUid && (
        <div className="mt-4 pt-4 border-t border-purple-200">
          <p className="text-sm text-purple-700">
            This listing is bound to NFC tag <code className="font-mono bg-purple-100 px-1 rounded">{currentNfcUid}</code>
          </p>
        </div>
      )}
    </div>
  )
}